import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  IonButtons, IonContent, IonHeader, IonIcon, IonLabel, IonPage, IonTabBar,
  IonTabButton, IonTitle, IonToolbar
} from "@ionic/react";
import {
  calendarOutline, folderOpenOutline, gridOutline, peopleOutline, chatbubblesOutline
} from "ionicons/icons";
import { ChevronDown, ChevronRight, CircleHelp, LogOut, MessageSquare, Search, Settings, ShieldCheck, X } from "lucide-react";
import { SECTIONS } from "@/pages/SectionPage";
import { scheduleScrollLockRelease } from "@/lib/scrollLock";

const LOGO_URL = "/thrively-logo.png";

// Main menu: the six sections, in order (Community has no tabs).
const navItems = [
  ...SECTIONS.filter(sec => sec.key !== "settings").map(sec => ({ path: sec.path, icon: sec.icon, label: sec.label })),
  { path: "/community", icon: MessageSquare, label: "Community" },
];

const isPathActive = (pathname, base) => pathname === base || pathname.startsWith(`${base}/`);

const RESULT_ROUTES = {
  child: "/team/children", carer: "/team/carers", provider: "/team/therapists",
  appointment: "/planner/calendar", session_note: "/planner/session-notes", goal: "/planner/goals",
  activity: "/planner/activities", budget: "/planner/budgets",
  document: "/resources/documents", article: "/resources/library", post: "/community"
};
const RESULT_LABELS = {
  child: "Children", carer: "Carers", provider: "Therapists", appointment: "Calendar",
  session_note: "Session Notes", goal: "Goals", activity: "Activities",
  document: "Documents", article: "Library", budget: "Budgets", post: "Community"
};

const mobileTabs = [
  { path: "/overview/summary", base: "/overview", label: "Home", icon: gridOutline },
  { path: "/planner/calendar", base: "/planner", label: "Planner", icon: calendarOutline },
  { path: "/team/children", base: "/team", label: "Team", icon: peopleOutline },
  { path: "/resources/documents", base: "/resources", label: "Resources", icon: folderOpenOutline },
  { path: "/community", base: "/community", label: "Community", icon: chatbubblesOutline }
];

function ProfileMenu({ user, onNavigate, onLogout, compact = false }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={compact
            ? "mobile-profile-trigger h-11 w-11 rounded-full p-0"
            : "h-11 gap-2 rounded-full border border-border bg-white px-2.5 hover:bg-muted"}
          aria-label="Open profile and settings"
          data-testid="profile-menu-trigger"
        >
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary text-xs font-bold text-primary-foreground">
              {user?.name?.charAt(0).toUpperCase() || "U"}
            </AvatarFallback>
          </Avatar>
          {!compact && (
            <>
              <span className="max-w-28 truncate text-sm font-medium text-foreground">{user?.name || "Profile"}</span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[60] w-64 rounded-2xl border-primary/10 p-2 shadow-xl">
        <DropdownMenuLabel className="px-3 py-2">
          <p className="truncate font-heading text-sm font-bold text-primary">{user?.name || "Your profile"}</p>
          <p className="truncate text-xs font-normal text-muted-foreground">{user?.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onNavigate("/help")} className="rounded-xl px-3 py-2.5">
          <CircleHelp className="text-primary" /> Help Center
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onNavigate("/settings/general")} className="rounded-xl px-3 py-2.5">
          <Settings className="text-primary" /> Settings
        </DropdownMenuItem>
        {user?.is_moderator && (
          <DropdownMenuItem onSelect={() => onNavigate("/settings/moderation")} className="rounded-xl px-3 py-2.5">
            <ShieldCheck className="text-primary" /> Moderation
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onLogout} className="rounded-xl px-3 py-2.5 text-destructive focus:bg-destructive/10 focus:text-destructive">
          <LogOut /> Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NavLink({ item, isActive, unreadCount, onNavigate }) {
  const showBadge = item.path === "/overview" && unreadCount > 0;
  return (
    <button
      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
      onClick={onNavigate}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left transition-all duration-200 ${
        isActive
          ? "bg-primary text-primary-foreground shadow-md"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      }`}
    >
      <item.icon className="h-5 w-5 flex-shrink-0" />
      <span className="font-medium">{item.label}</span>
      {showBadge && (
        <span
          className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? "bg-white text-primary" : "bg-destructive text-white"}`}
          data-testid="inbox-unread-badge"
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
      {!showBadge && isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
    </button>
  );
}

function SearchPanel({ searchQ, setSearchQ, searching, searchResults, onResultClick }) {
  return (
    <div className="relative flex-1 max-w-2xl" data-testid="universal-search">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        value={searchQ}
        onChange={(e) => setSearchQ(e.target.value)}
        placeholder="Search everything…"
        className="pl-9 pr-9 rounded-full bg-muted/50 border-0 focus:bg-white focus:ring-2 focus:ring-primary/30"
        data-testid="universal-search-input"
      />
      {searchQ && (
        <button
          onClick={() => setSearchQ("")}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {searchQ && (
        <div
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-border max-h-[60vh] overflow-y-auto z-50"
          data-testid="universal-search-dropdown"
        >
          {searching ? (
            <p className="p-4 text-sm text-muted-foreground">Searching…</p>
          ) : !searchResults || searchResults.total === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No results for &quot;{searchQ}&quot;</p>
          ) : (
            <div className="py-2">
              {Object.entries(searchResults.results).map(([type, items]) => (
                <div key={type} className="mb-2">
                  <p className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{RESULT_LABELS[type] || type}</p>
                  {items.map(item => (
                    <button
                      key={`${type}-${item.id}`}
                      onClick={() => onResultClick(type, item.id)}
                      className="w-full text-left px-4 py-2 hover:bg-muted/50 transition-colors"
                      data-testid={`search-result-${item.id}`}
                    >
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      {item.tags && item.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {item.tags.slice(0, 4).map(t => (
                            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">{t}</span>
                          ))}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Safety net: if a Radix overlay (dialog/select) was unmounted mid-close by a
  // navigation, its body scroll lock can be left behind, freezing page scroll.
  // On every route/tab change, clear any stale lock once no overlay is open.
  useEffect(() => {
    const content = document.querySelector(".dashboard-scroll-content");
    requestAnimationFrame(() => content?.scrollToTop?.(0));
    return scheduleScrollLockRelease();
  }, [location.pathname]);
  // Poll unread count every 60s + on route change
  useEffect(() => {
    let mounted = true;
    const fetchUnread = async () => {
      try {
        const r = await apiClient.get("/notifications/unread-count");
        if (mounted) setUnreadCount(r.data.count || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, [location.pathname]);

  // Debounced universal search
  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults(null); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await apiClient.get(`/search?q=${encodeURIComponent(searchQ)}`);
        setSearchResults(r.data);
      } catch { setSearchResults({ results: {}, total: 0 }); }
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [searchQ]);

  const RESULT_ROUTES_INNER = RESULT_ROUTES;

  const handleSearchResultClick = (type) => {
    navigate(RESULT_ROUTES_INNER[type] || "/");
    setSearchQ("");
    setSearchResults(null);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleProfileNavigation = (path) => {
    navigate(path);
  };

  return (
    <IonPage className="native-app-page">
      <IonHeader className="mobile-native-header lg:hidden" translucent>
        <IonToolbar>
          <IonTitle>
            <img src={LOGO_URL} alt="Thrively" className="h-11 w-auto" />
          </IonTitle>
          <IonButtons slot="end">
            <ProfileMenu user={user} onNavigate={handleProfileNavigation} onLogout={handleLogout} compact />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      {/* Sidebar */}
      <aside
        className="hidden lg:block fixed top-0 left-0 z-50 h-full w-72 bg-white border-r border-border"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border">
            <div className="flex flex-col items-start gap-1">
              <img src={LOGO_URL} alt="Thrively" className="h-20 w-auto" />
              <div className="flex items-center gap-2 pl-1">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700">
                  Beta
                </span>
                <span className="text-[11px] text-muted-foreground font-mono">v0.5</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-4 py-6">
            <nav className="space-y-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  item={item}
                  isActive={isPathActive(location.pathname, item.path)}
                  unreadCount={unreadCount}
                  onNavigate={() => navigate(item.path)}
                />
              ))}
            </nav>
          </ScrollArea>

        </div>
      </aside>

      <IonContent className="dashboard-scroll-content">
        {/* Main Content */}
        <main className="lg:ml-72 min-h-screen pb-[calc(5.5rem+env(safe-area-inset-bottom))] lg:pb-0">
          {/* Desktop top bar with universal search */}
          <div className="hidden lg:flex sticky top-0 z-30 gap-4 bg-white/80 backdrop-blur-md border-b border-border px-6 py-3 items-center">
            <SearchPanel
              searchQ={searchQ}
              setSearchQ={setSearchQ}
              searching={searching}
              searchResults={searchResults}
              onResultClick={handleSearchResultClick}
            />
            <ProfileMenu user={user} onNavigate={handleProfileNavigation} onLogout={handleLogout} />
          </div>
          {/* Mobile search */}
          <div className="lg:hidden px-4 pt-3 pb-2 bg-background">
            <SearchPanel
              searchQ={searchQ}
              setSearchQ={setSearchQ}
              searching={searching}
              searchResults={searchResults}
              onResultClick={handleSearchResultClick}
            />
          </div>
          <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </IonContent>

      <IonTabBar slot="bottom" className="mobile-native-tab-bar lg:hidden" data-testid="mobile-tab-bar">
        {mobileTabs.map((tab) => (
          <IonTabButton
            key={tab.base}
            tab={tab.label.toLowerCase()}
            selected={isPathActive(location.pathname, tab.base)}
            onClick={() => navigate(tab.path)}
          >
            <IonIcon icon={tab.icon} />
            <IonLabel>{tab.label}</IonLabel>
          </IonTabButton>
        ))}
      </IonTabBar>
    </IonPage>
  );
}
